import { Controller } from "@hotwired/stimulus"

/**
* Stimulus controller for managing saved form views and filter states
* Handles loading/saving form parameters, active search indicators, and form population
*
* Usage:
* <form data-controller="saved-form-view--form"
*       data-saved-form-view--form-target="form"
*       data-saved-form-view--form-exclude-fields-value="csrf_token,page">
*   <div data-saved-form-view--form-target="filtersHeader">
*     <h5 data-saved-form-view--form-target="filtersTitle">Filters</h5>
*     <button data-saved-form-view--form-target="clearButton" data-action="saved-form-view--form#clearActiveSearch">Clear</button>
*   </div>
* </form>
*/
export default class extends Controller {
  static targets = ["form", "filtersHeader", "filtersTitle", "clearButton", "accordionButton"]
  static values = {
    excludeFields: String,
    activeSearchId: String,
    activeSearchName: String,
    editable: Boolean
  }

  connect() {
    document.addEventListener('saved-form-view:load', this.loadSearch.bind(this))
    document.addEventListener('saved-form-view:check-active', this.handleActiveCheck.bind(this))
    this.updateSaveButtonState()

    document.addEventListener('saved-form-view:updated', this.handleViewUpdated.bind(this))
  }

  disconnect() {
    document.removeEventListener('saved-form-view:load', this.loadSearch.bind(this))
    document.removeEventListener('saved-form-view:check-active', this.handleActiveCheck.bind(this))
    document.removeEventListener('saved-form-view:updated', this.handleViewUpdated.bind(this))

    this.activeSearchIdValue = ''
    this.activeSearchNameValue = ''
  }

  handleViewUpdated(event) {
    const { searchId, searchName } = event.detail

    // Only update if this is the currently active view
    if (this.activeSearchIdValue === searchId) {
      this.activeSearchNameValue = searchName
      this.showActiveSearchIndicator()
      this.updateSaveButtonState()
    }
  }

  loadSearch(event) {
    const params = event.detail.searchParams
    const searchId = event.detail.searchId
    const searchName = event.detail.searchName

    this.populateForm(params)

    this.activeSearchIdValue = searchId
    this.activeSearchNameValue = searchName
    this.showActiveSearchIndicator()
    this.updateSaveButtonState()
  }

  handleActiveCheck(event) {
    const editingSearchId = event.detail.editingSearchId
    const editingSearchName = event.detail.editingSearchName
    
    if (this.activeSearchIdValue && this.activeSearchIdValue !== editingSearchId) {
      const activeSearchName = this.activeSearchNameValue
      const message = `You currently have "${activeSearchName}" loaded in the form, but you're about to edit "${editingSearchName}". Saving will overwrite "${editingSearchName}" with the current form values. Continue?`
      
      const confirmed = confirm(message)
      
      // Send back the result
      event.target.dispatchEvent(new CustomEvent('saved-form-view:check-result', {
        detail: { confirmed, editingSearchId },
        bubbles: true
      }))
    } else {
      // No conflict, proceed
      event.target.dispatchEvent(new CustomEvent('saved-form-view:check-result', {
        detail: { confirmed: true, editingSearchId },
        bubbles: true
      }))
    }
  }

showActiveSearchIndicator() {
  const header = this.filtersHeaderTarget
  const title = this.filtersTitleTarget
  const clearButton = this.clearButtonTarget
  const accordionButton = this.accordionButtonTarget

  header.classList.add('bg-info', 'text-white')
  title.innerHTML = `Filters: ${this.activeSearchNameValue}`
  clearButton.classList.remove('d-none')

  accordionButton.classList.remove('btn-outline-primary')
  accordionButton.classList.add('btn-outline-light', 'bg-white')

  const existingIndicator = this.element.querySelector('.active-search-indicator')
  if (existingIndicator) existingIndicator.remove()
}

clearActiveSearch() {
  this.activeSearchIdValue = ''
  this.activeSearchNameValue = ''

  const header = this.filtersHeaderTarget
  const title = this.filtersTitleTarget
  const clearButton = this.clearButtonTarget
  const accordionButton = this.accordionButtonTarget

  header.classList.remove('bg-info', 'text-white')
  accordionButton.classList.add('btn-outline-primary')
  accordionButton.classList.remove('btn-outline-light', 'bg-white')
  title.textContent = 'Filters'
  clearButton.classList.add('d-none')

  this.updateSaveButtonState()
}


  updateSaveButtonState() {
    const saveButton = document.querySelector('[data-save-view-button]')
    if (!saveButton) return

    if (this.activeSearchIdValue) {
      // Edit mode
      saveButton.textContent = 'Update view'
      saveButton.setAttribute('data-action', 'click->saved-form-view--modal#showEditModal')
      saveButton.setAttribute('data-search-id', this.activeSearchIdValue)
      saveButton.setAttribute('data-search-name', this.activeSearchNameValue)
    } else {
      // Create mode
      saveButton.textContent = 'Add View'
      saveButton.setAttribute('data-action', 'click->saved-form-view--modal#showCreateModal')
      saveButton.removeAttribute('data-search-id')
      saveButton.removeAttribute('data-search-name')
    }
  }

  populateForm(params) {
    // Prevent the automatic form submission stuff
    this.formTarget.dispatchEvent(new CustomEvent('search:pause', { bubbles: true }))

    // Clear first
    const inputs = this.formTarget.querySelectorAll('input, select, textarea')
    inputs.forEach(input => {
      if (input.type === 'checkbox' || input.type === 'radio') {
        input.checked = false
      } else if (input.tagName === 'SELECT') {
        if (input.multiple) {
          Array.from(input.options).forEach(option => option.selected = false)
        } else {
          input.selectedIndex = 0 // Reset to first option (usually empty/default)
        }

        // Clear Select2 if present
        if (window.jQuery && window.jQuery(input).data('select2')) {
          window.jQuery(input).val(null).trigger('change')
        }
      } else {
        input.value = ''
      }
    })

    // Load params from saved view
    Object.entries(params).forEach(([key, value]) => {
      const input = this.formTarget.querySelector(`[name*="${key}"]`)
      if (!input) return

      if (input.type === 'checkbox' || input.type === 'radio') {
        if (Array.isArray(value)) {
          input.checked = value.includes(input.value)
        } else {
          input.checked = value == input.value || value === true
        }
      } else if (input.tagName === 'SELECT') {
        if (input.multiple) {
          Array.from(input.options).forEach(option => {
            option.selected = Array.isArray(value) ? value.includes(option.value) : false
          })
        } else {
          input.value = value
        }

        // Trigger Select2 update if present
        if (window.jQuery && window.jQuery(input).data('select2')) {
          window.jQuery(input).trigger('change')
        }
      } else {
        input.value = value || ''
      }
    })

    // Trigger the existing form change events that the portfolio controller already listens for
    this.formTarget.dispatchEvent(new Event('change', { bubbles: true }))
    
    // Scroll the form into view, accounting for sticky nav
    this.formTarget.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'center' 
    })

    setTimeout(() => {
      this.formTarget.dispatchEvent(new CustomEvent('search:unpause', { bubbles: true }))
    }, 100)

  }

  getCurrentParams() {
    const formData = new FormData(this.formTarget)
    const searchParams = {}
    const excludedFields = this.getExcludedFields()

    for (let [key, value] of formData.entries()) {
      // Skip excluded fields
      if (excludedFields.includes(key)) continue

      if (searchParams[key]) {
        if (Array.isArray(searchParams[key])) {
          searchParams[key].push(value)
        } else {
          searchParams[key] = [searchParams[key], value]
        }
      } else {
        searchParams[key] = value
      }
    }

    return searchParams
  }

  getExcludedFields() {
    const defaultExclusions = ['authenticity_token', 'utf8']
    const customExclusions = this.hasExcludeFieldsValue ? 
      this.excludeFieldsValue.split(',').map(f => f.trim()) : []

    return [...defaultExclusions, ...customExclusions]
  }
}
